import type { CanonicalPresentationCondition, CanonicalPresentationModelV1 } from "@enrein/shared-contracts";
import type { ReactNode } from "react";

const CONDITION_MARK: Record<CanonicalPresentationCondition, string> = {
  READY: "Ready",
  WAITING: "Waiting",
  IN_PROGRESS: "In progress",
  NEEDS_ATTENTION: "Needs attention",
  COMPLETE: "Complete",
};

export function Condition({ condition }: { readonly condition: CanonicalPresentationCondition }) {
  return (
    <span className="ws-condition" data-condition={condition}>
      <span className="ws-condition-mark" aria-hidden="true">
        {condition === "NEEDS_ATTENTION" ? "!" : condition === "COMPLETE" ? "✓" : "•"}
      </span>
      {CONDITION_MARK[condition]}
    </span>
  );
}

export function StartHere({
  model,
  onPrimary,
}: {
  readonly model: CanonicalPresentationModelV1;
  readonly onPrimary: () => void;
}) {
  return (
    <section className="ws-card" aria-labelledby="start-here-title">
      <p className="ws-muted">{model.journeyStage}</p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <h2 id="start-here-title" className="ws-title">
          {model.title}
        </h2>
        <Condition condition={model.condition} />
      </div>
      <p>{model.reason}</p>
      {model.relevantRecords && model.relevantRecords.length > 0 ? (
        <p className="ws-muted">{model.relevantRecords[0]}</p>
      ) : null}
      {model.progress ? <p className="ws-muted">{model.progress.label}</p> : null}
      {model.primaryAction ? (
        <button type="button" className="ws-btn ws-btn-primary" onClick={onPrimary}>
          {model.primaryAction.label}
        </button>
      ) : null}
    </section>
  );
}

export function ExactVersionChip({
  role,
  versionLabel,
}: {
  readonly role: "Working" | "Client-visible" | "Approved" | "Live";
  readonly versionLabel: string;
}) {
  return (
    <span className="ws-version-chip">
      <strong>{role}</strong>
      <span>{versionLabel}</span>
    </span>
  );
}

export function FailureGrammar({
  title,
  detail,
  onRetry,
}: {
  readonly title: string;
  readonly detail: string;
  readonly onRetry?: () => void;
}) {
  return (
    <div className="ws-global-notice" role="alert">
      <strong>{title}</strong>
      <p>{detail}</p>
      {onRetry ? (
        <button type="button" className="ws-btn" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function Table({
  columns,
  rows,
  empty,
}: {
  readonly columns: readonly string[];
  readonly rows: ReadonlyArray<readonly ReactNode[]>;
  readonly empty: string;
}) {
  if (rows.length === 0) {
    return <p className="ws-muted">{empty}</p>;
  }
  return (
    <div className="ws-table-wrap">
      <table className="ws-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Toast({ message }: { readonly message: string | null }) {
  if (!message) return null;
  return (
    <div className="ws-toast" role="status">
      {message}
    </div>
  );
}

export function GlobalNotice({ children }: { readonly children: ReactNode }) {
  return <div className="ws-global-notice">{children}</div>;
}

export function ChangeDiff({ before, after }: { readonly before: string; readonly after: string }) {
  return (
    <div className="ws-change-diff">
      <div>
        <h3 className="ws-title">Before</h3>
        <pre>{before}</pre>
      </div>
      <div>
        <h3 className="ws-title">After</h3>
        <pre>{after}</pre>
      </div>
    </div>
  );
}
